import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PostIts.scss";
import PostIt from "./PostItComponents/PostIt";
import {
  getPostIts,
  createPostIt,
  updatePostIt,
  deletePostIt,
} from "../../api/postIts";
import useAuthStore from "../../store/authStore";
import { useResync } from "../../hooks/useRealtime";

function PostIts() {
  const { t } = useTranslation();
  const [postIts, setPostIts] = useState([]);

  const user = useAuthStore((state) => state.user);
  const token = useAuthStore((state) => state.token);
  const userId = user?._id || user?.id;

  useEffect(() => {
    if (token) {
      loadPostIts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Reload when this tab may have missed changes made on another device
  useResync(() => {
    if (token) loadPostIts();
  });

  // Listen for unpin events from PinnedPostIts to add them back
  useEffect(() => {
    const handleUnpinned = (e) => {
      const postIt = e.detail;
      // Update the post-it in the overlay (it may already exist from initial fetch)
      setPostIts((prev) => {
        const exists = prev.find((p) => p._id === postIt._id);
        if (exists) {
          // Update existing post-it with new data (including isPinned: false)
          return prev.map((p) => (p._id === postIt._id ? postIt : p));
        } else {
          // Add if it doesn't exist
          return [...prev, postIt];
        }
      });
    };

    window.addEventListener("postit-unpinned", handleUnpinned);

    return () => {
      window.removeEventListener("postit-unpinned", handleUnpinned);
    };
  }, []);

  const loadPostIts = async () => {
    try {
      const data = await getPostIts(token);
      setPostIts(data);
    } catch (error) {
      console.error("Failed to load post-its:", error);
    }
  };

  const handleAddPostIt = async () => {
    if (!userId || !token) {
      console.error("No userId or token found - user not logged in?");
      alert(t("postIts.loginRequired"));
      return;
    }

    try {
      // Calculate the highest z-index so new post-it appears on top
      const maxZ = Math.max(...postIts.map((p) => p.zIndex || 1), 1);
      const newZIndex = maxZ + 1;

      const newPostIt = await createPostIt(
        {
          content: "",
          color: "#f2dd3d",
          position: { x: 20, y: 90 },
          zIndex: newZIndex,
        },
        token,
      );
      setPostIts([...postIts, newPostIt]);
    } catch (error) {
      console.error("Failed to create post-it:", error);
      alert(t("postIts.createFailed", { error: error.message }));
    }
  };

  const handleUpdatePostIt = async (id, updates) => {
    try {
      // Send the revision this change is based on, so a stale tab can't
      // overwrite changes made on another device
      const rev = updates.rev ?? postIts.find((p) => p._id === id)?.rev;
      const updatedPostIt = await updatePostIt(
        id,
        { ...updates, rev },
        token,
      );

      // If pinning, remove from overlay list (it will show in PinnedPostIts)
      if (updates.isPinned === true) {
        setPostIts((prev) => prev.filter((p) => p._id !== id));
        // Notify PinnedPostIts component to add it
        window.dispatchEvent(
          new CustomEvent("postit-pinned", { detail: updatedPostIt }),
        );
      }
      // For any other update, just update the post-it in place
      else {
        setPostIts((prev) =>
          prev.map((p) => (p._id === id ? updatedPostIt : p)),
        );
      }
    } catch (error) {
      console.error("Failed to update post-it:", error);
    }
  };

  const handleDeletePostIt = async (id) => {
    try {
      await deletePostIt(id, token);
      setPostIts(postIts.filter((p) => p._id !== id));
    } catch (error) {
      console.error("Failed to delete post-it:", error);
    }
  };

  const handleBringToFront = async (id) => {
    const maxZ = Math.max(...postIts.map((p) => p.zIndex || 1), 1);
    const newZIndex = maxZ + 1;

    // Use handleUpdatePostIt to ensure proper state update
    await handleUpdatePostIt(id, { zIndex: newZIndex });
  };

  return (
    <div className="postits-wrapper">
      <div className="postits-wrapper">
        <div className="postits-header">
          <button className="add-postit-button" onClick={handleAddPostIt}>
            <span className="material-symbols-outlined">add</span>
            <span>{t("postIts.add")}</span>
          </button>
        </div>
        <div className="postits-content">
          {postIts
            .filter((postIt) => !postIt.isPinned) // Only show non-pinned post-its here
            .map((postIt) => (
              <PostIt
                key={postIt._id}
                postIt={postIt}
                onUpdate={handleUpdatePostIt}
                onDelete={handleDeletePostIt}
                onBringToFront={handleBringToFront}
              />
            ))}
        </div>
      </div>
    </div>
  );
}

export default PostIts;
